import React, { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { formatEUR } from "@/lib/utils";
import { fmtData } from "@/lib/listaColonne";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { StatoBadge, errore, TornaIndietro, LINK, TipoSoggetto, SceltaSoggetto } from "./crmComuni";
import { RiquadroContatto } from "./Recapiti";
import UnisciContatti from "./UnisciContatti";
import { VoceNota } from "./CrmOpportunitaScheda";

const lista = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);

export default function CrmContattoScheda() {
  const { id } = useParams();
  const [c, setC] = useState(null);
  const [f, setF] = useState(null);
  const [nonTrovato, setNonTrovato] = useState(false);
  const [unisci, setUnisci] = useState(false);
  const carica = useCallback(() => api.get(`/crm/contatti/${id}`).then((r) => {
    setC(r.data);
    setF({ nome: r.data.nome, telefoni: (r.data.telefoni || []).join(", "), email: (r.data.email || []).join(", "),
      altri_recapiti: r.data.altri_recapiti || "", consenso: r.data.consenso?.valore ?? null,
      tipo_soggetto: r.data.tipo_soggetto || "", comune: r.data.comune || "", provincia: r.data.provincia || "" });
  }).catch(() => setNonTrovato(true)), [id]);
  useEffect(() => { carica(); }, [carica]);
  if (nonTrovato) return <Card className="p-8 text-center text-slate-500" data-testid="crm-contatto-404">Contatto non trovato.</Card>;
  if (!c || !f) return <div className="text-slate-500">Caricamento...</div>;
  const salva = async () => {
    try {
      const daAnag = c.dati_da_anagrafica;  // Sprint CRM-3 — collegato: i dati dell'anagrafica non si modificano qui
      await api.put(`/crm/contatti/${id}`, { ...(daAnag ? {} : { nome: f.nome, telefoni: lista(f.telefoni), email: lista(f.email),
        tipo_soggetto: f.tipo_soggetto, comune: f.comune, provincia: f.provincia }), altri_recapiti: f.altri_recapiti,
        ...(f.consenso === null ? {} : { consenso: f.consenso }) });
      toast.success("Salvato"); carica();
    } catch (e) { toast.error(errore(e)); }
  };
  return (
    <div className="space-y-4" data-testid="crm-contatto-page">
      <TornaIndietro to="/crm/contatti" testo="Torna ai contatti" testid="crm-contatto-indietro" />
      <div className="flex items-center gap-3">
        <h1 className="font-heading text-2xl text-slate-900" data-testid="crm-contatto-nome">{c.nome}</h1>
        <TipoSoggetto tipo={c.tipo_soggetto} testid="crm-contatto-tipo" />
      </div>
      {/* Sprint CRM-2A.1 — tutti i recapiti per esteso; l'interazione si collega a un'opportunità in corso visibile */}
      <Card className="p-4"><RiquadroContatto c={c} opportunita={c.opportunita.filter((o) => o.stato === "aperta")} onRegistrata={carica}
        prefix="crm-contatto-recapiti" /></Card>
      <Card className="p-4 space-y-3">
        {c.dati_da_anagrafica && <div className="rounded bg-sky-50 border border-sky-200 p-2 text-sm" data-testid="crm-contatto-da-anagrafica">
          Nome, recapiti, tipo, comune e provincia sono letti dall'anagrafica{" "}
          <Link className="text-sky-700 hover:underline" to={`/anagrafiche/${c.anagrafica_id}`} data-testid="crm-contatto-link-anagrafica">{c.anagrafica_codice || "apri"}</Link>
          {" "}e si modificano lì.</div>}
        <fieldset disabled={!!c.dati_da_anagrafica} className="grid grid-cols-2 gap-3" data-testid="crm-contatto-campi-anagrafica">
          <div><Label>Nome</Label><Input value={f.nome} onChange={(e) => setF({ ...f, nome: e.target.value })} data-testid="crm-contatto-input-nome" /></div>
          <div><Label>Privato o azienda</Label><SceltaSoggetto value={f.tipo_soggetto} onChange={(v) => setF({ ...f, tipo_soggetto: v })} prefix="crm-contatto-soggetto" /></div>
          <div><Label>Comune</Label><Input value={f.comune} onChange={(e) => setF({ ...f, comune: e.target.value })} data-testid="crm-contatto-comune" /></div>
          <div><Label>Provincia</Label><Input value={f.provincia} maxLength={2} onChange={(e) => setF({ ...f, provincia: e.target.value.toUpperCase() })} data-testid="crm-contatto-provincia" /></div>
          <div><Label>Telefoni (separati da virgola)</Label><Input value={f.telefoni} onChange={(e) => setF({ ...f, telefoni: e.target.value })} data-testid="crm-contatto-telefoni" /></div>
          <div><Label>Email (separate da virgola)</Label><Input value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="crm-contatto-email" /></div>
        </fieldset>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Altri recapiti</Label><Input value={f.altri_recapiti} onChange={(e) => setF({ ...f, altri_recapiti: e.target.value })} data-testid="crm-contatto-altri" /></div>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <Checkbox checked={!!f.consenso} onCheckedChange={(v) => setF({ ...f, consenso: !!v })} data-testid="crm-contatto-consenso" />
          Consenso a ricevere proposte
        </label>
        <div className="text-xs text-slate-500" data-testid="crm-contatto-consenso-info">
          {c.consenso ? `${c.consenso.valore ? "Sì" : "No"} · registrato il ${fmtData(c.consenso.data)} da ${c.consenso.registrato_da_nome || "—"}` : "Consenso non registrato"}
        </div>
        <div className="text-xs text-slate-500" data-testid="crm-contatto-ultima-attivita">Ultima attività: {fmtData(c.ultima_attivita)}</div>
        <div className="flex justify-between">
          <Button variant="outline" onClick={() => setUnisci(true)} data-testid="crm-contatto-unisci">Unisci con…</Button>
          <Button onClick={salva} className="bg-[#0F4C81] hover:bg-[#0c3d68]" data-testid="crm-contatto-salva">Salva</Button>
        </div>
      </Card>
      {unisci && <UnisciContatti c={c} onChiudi={() => setUnisci(false)} />}
      <Card className="p-4" data-testid="crm-contatto-interazioni">
        <div className="font-heading text-sm uppercase text-slate-500 tracking-wide mb-2">Interazioni</div>
        {(c.interazioni || []).map((n) => <VoceNota key={n.id} n={n} extra={<> · <Link className={LINK} to={`/crm/opportunita/${n.opportunita_id}`}>{n.opportunita_descrizione}</Link></>} />)}
        {!c.interazioni?.length && <div className="text-sm text-slate-400">Nessuna interazione</div>}
      </Card>
      <Card className="p-4">
        <div className="font-heading text-sm uppercase text-slate-500 tracking-wide mb-2">Opportunità</div>
        {c.opportunita.map((o) => (
          <Link key={o.id} to={`/crm/opportunita/${o.id}`} className="flex items-center gap-3 border-b py-2 text-sm hover:bg-slate-50" data-testid={`crm-contatto-opp-${o.id}`}>
            <span className="flex-1">{o.descrizione}</span>
            <span className="text-slate-500" data-testid={`crm-contatto-opp-azienda-${o.id}`}>Azienda: {o.azienda_nome || "—"}</span>
            <span className="text-slate-500" data-testid={`crm-contatto-opp-pipeline-${o.id}`}>Pipeline: {o.pipeline_nome || "—"}</span>
            <span className="text-slate-500" data-testid={`crm-contatto-opp-sede-${o.id}`}>{o.sede_nome || "—"}</span>
            <span className="text-slate-500">{o.fase?.nome || "—"}</span>
            <StatoBadge stato={o.stato} /><span className="font-mono w-24 text-right" data-testid={`crm-contatto-opp-prezzo-${o.id}`}>{formatEUR(o.prezzo_vendita_mostrato)}</span>
          </Link>
        ))}
        {!c.opportunita.length && <div className="text-sm text-slate-400">Nessuna opportunità visibile</div>}
      </Card>
    </div>
  );
}
